// src/features/home/components/MyCoursesSlide.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, ProgressBar, Spinner } from "react-bootstrap";
import {
  BsChevronLeft,
  BsChevronRight,
  BsPlayBtn,
  BsPlayCircle,
} from "react-icons/bs";

import "bootstrap/dist/css/bootstrap.min.css";
import "../../../shared/styles/CardSlideForm.css";
import "../../../shared/styles/UtillitiesComponent.css";

import { db, auth } from "../../../shared/libs/firebaseConfig";
import CourseAccessGate from "../../../shared/components/CourseAccessGate";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import InstallmentCourseEntryGuard from "../../../shared/components/InstallmentCourseEntryGuard";

function toDateSafe(v) {
  try {
    if (v?.toDate) return v.toDate();
    if (v instanceof Date) return v;
    if (typeof v === "number") return new Date(v);
    if (typeof v === "string") return new Date(v);
  } catch {}
  return null;
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function formatPercent(value) {
  const n = Number(value || 0);
  if (n >= 100) return 100;
  if (n <= 0) return 0;
  return Math.round(n);
}

function normalizeAllowedHeaderTypes(value) {
  if (!value) return null;
  if (!Array.isArray(value)) return null;

  const arr = value
    .map((x) => String(x || "").trim())
    .filter(Boolean);

  return arr.length ? Array.from(new Set(arr)) : null;
}

function mergeAllowedHeaderTypesFromEnrolls(enrolls = []) {
  const merged = new Set();

  for (const e of enrolls) {
    const arr = normalizeAllowedHeaderTypes(e?.allowedHeaderTypes);
    if (!arr) continue;
    for (const v of arr) merged.add(v);
  }

  return merged.size ? Array.from(merged) : null;
}

function courseMatchesCategory(course, selectedCategory) {
  if (!selectedCategory || selectedCategory === "all") return true;

  const categories = Array.isArray(course?.categories) ? course.categories : [];
  const courseType = String(course?.courseType || "").trim();

  if (categories.includes(selectedCategory)) return true;

  const mapCourseTypeToCategory = {
    thaibar: "เนติบัณฑิต",
    attorney: "ทนายความ",
    prosecutor: "อัยการ",
    judge: "ผู้พิพากษา",
    ocsc: "สอบ ก.พ.",
  };

  return mapCourseTypeToCategory[courseType] === selectedCategory;
}

/**
 * ดึง enrolls ที่ผู้ใช้ได้รับสิทธิ์แล้ว
 * ปรับ field ได้ตาม schema จริงของคุณ
 */
async function fetchMyGrantedEnrollments(uid, maxFetch = 30) {
  const q1 = query(
    collection(db, "enrolls"),
    where("userId", "==", uid),
    where("accessStatus", "==", "granted"),
    orderBy("updatedAt", "desc"),
    limit(maxFetch)
  );

  try {
    const snap = await getDocs(q1);
    if (!snap.empty) {
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    }
  } catch (e) {
    console.warn("fetchMyGrantedEnrollments q1 failed:", e?.message);
  }

  const q2 = query(
    collection(db, "enrolls"),
    where("uid", "==", uid),
    where("accessStatus", "==", "granted"),
    limit(maxFetch)
  );

  try {
    const snap = await getDocs(q2);
    if (!snap.empty) {
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    }
  } catch (e) {
    console.warn("fetchMyGrantedEnrollments q2 failed:", e?.message);
  }

  return [];
}

async function fetchCourseProgress(uid, courseId) {
  try {
    const ref = doc(db, "users", uid, "courseProgress", courseId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (e) {
    console.warn("fetchCourseProgress failed:", e?.message);
    return null;
  }
}

async function fetchCourseDoc(courseId) {
  try {
    const ref = doc(db, "courses", courseId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (e) {
    console.warn("fetchCourseDoc failed:", e?.message);
    return null;
  }
}

/**
 * ดึงเฉพาะ lessons ที่ published + active + ready + มี duration
 * และถ้ามี allowedHeaderTypes จะกรองเฉพาะ headerType ที่ผู้ใช้มีสิทธิ์ดู
 */
/* async function fetchPublishedVideoLessonsWithDuration(courseId, allowedHeaderTypes = null) {
  const result = [];

  const allowedSet =
    Array.isArray(allowedHeaderTypes) && allowedHeaderTypes.length > 0
      ? new Set(allowedHeaderTypes.map((x) => String(x || "").trim()).filter(Boolean))
      : null;

  try {
    const headersSnap = await getDocs(
      query(collection(db, "courses", courseId, "headers"), orderBy("order", "asc"))
    );

    for (const headerDoc of headersSnap.docs) {
      const headerId = headerDoc.id;
      const header = headerDoc.data();
      const headerType = String(header?.headerType || "").trim();

      if (allowedSet && !allowedSet.has(headerType)) {
        continue;
      }

      const sectionsSnap = await getDocs(
        query(
          collection(db, "courses", courseId, "headers", headerId, "sections"),
          orderBy("order", "asc")
        )
      );

      for (const sectionDoc of sectionsSnap.docs) {
        const sectionId = sectionDoc.id;

        const itemsSnap = await getDocs(
          query(
            collection(
              db,
              "courses",
              courseId,
              "headers",
              headerId,
              "sections",
              sectionId,
              "items"
            ),
            orderBy("order", "asc")
          )
        );

        for (const itemDoc of itemsSnap.docs) {
          const item = itemDoc.data();
          const durationSec = Number(item?.video?.durationSec || 0);

          const isPublished = item?.contentStatus === "published";
          const isActive = item?.isActive !== false;
          const isVideoReady =
            item?.video?.isReady === true ||
            item?.video?.readyToStream === true ||
            item?.video?.status === "ready";

          if (isPublished && isActive && isVideoReady && durationSec > 0) {
            result.push({
              id: itemDoc.id,
              courseId,
              headerId,
              headerType,
              sectionId,
              title: item?.title || "",
              durationSec,
            });
          }
        }
      }
    }
  } catch (e) {
    console.warn(
      `fetchPublishedVideoLessonsWithDuration failed (${courseId}):`,
      e?.message
    );
  }

  return result;
} */

/* function computeCourseProgress(lessons, courseProgress) {
  const totalDurationSec = lessons.reduce((sum, x) => sum + Number(x.durationSec || 0), 0);

  const states = courseProgress?.lessonStates || {};
  let completedDurationSec = 0;

  for (const lesson of lessons) {
    const state = states?.[lesson.id];
    if (state?.completed === true) {
      completedDurationSec += Number(lesson.durationSec || 0);
    }
  }

  const rawPercent =
    totalDurationSec > 0 ? (completedDurationSec / totalDurationSec) * 100 : 0;

  const percent = clamp(rawPercent, 0, 100);
  const isComplete = percent >= 100 && totalDurationSec > 0;

  return {
    totalDurationSec,
    completedDurationSec,
    percent,
    percentLabel: formatPercent(percent),
    isComplete,
  };
} */

function buildProgressFromSummary(progress) {
  const percent = formatPercent(progress?.percent || 0);
  const totalDurationSec = Number(progress?.totalDurationSec || 0);
  const completedDurationSec = Number(progress?.completedDurationSec || 0);

  return {
    percent,
    percentLabel: percent,
    totalDurationSec,
    completedDurationSec,
    completedLessons: Number(progress?.completedLessons || 0),
    totalLessons: Number(progress?.totalLessons || 0),
    isComplete: percent >= 100 && totalDurationSec > 0,
  };
}

export default function MyCoursesSlide({
  title = "คอร์สเรียนของฉัน",
  moreTo = "/account/my-courses",
  moreLabel = "คอร์สเพิ่มเติม",
  maxFetch = 30,
  selectedCategory = "all",
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const uid = auth?.currentUser?.uid;
        if (!uid) {
          if (!alive) return;
          setRows([]);
          return;
        }

        const enrolls = await fetchMyGrantedEnrollments(uid, maxFetch);

        const enrollsByCourseId = enrolls.reduce((acc, e) => {
          const courseId = e?.courseId;
          if (!courseId) return acc;
          if (!acc[courseId]) acc[courseId] = [];
          acc[courseId].push(e);
          return acc;
        }, {});

        const courseIds = Object.keys(enrollsByCourseId);

        const data = await Promise.all(
          courseIds.map(async (courseId) => {
            const courseEnrolls = enrollsByCourseId[courseId] || [];
            const allowedHeaderTypes = mergeAllowedHeaderTypesFromEnrolls(courseEnrolls);

            const [course, progress] = await Promise.all([
              fetchCourseDoc(courseId),
              fetchCourseProgress(uid, courseId),
            ]);

            if (!course) return null;

            const computed = buildProgressFromSummary(progress);

            return {
              id: courseId,
              slug: course?.slug || courseId,
              title: course?.title || "คอร์สเรียน",
              subTitle: course?.subTitle || "",
              imageCoverUrl: course?.imageCoverUrl || "",
              categories: Array.isArray(course?.categories) ? course.categories : [],
              courseType: course?.courseType || "",
              updatedAt: course?.updatedAt || course?.createdAt || null,
              lastOpenedAt: progress?.lastOpenedAt || null,
              enrolls: courseEnrolls,
              allowedHeaderTypes,
              progress,
              ...computed,
            };
          })
        );

        const cleanRows = data.filter(Boolean);

        cleanRows.sort((a, b) => {
          const aLast = toDateSafe(a.lastOpenedAt)?.getTime() || 0;
          const bLast = toDateSafe(b.lastOpenedAt)?.getTime() || 0;
          if (aLast !== bLast) return bLast - aLast;

          const aUpd = toDateSafe(a.updatedAt)?.getTime() || 0;
          const bUpd = toDateSafe(b.updatedAt)?.getTime() || 0;
          return bUpd - aUpd;
        });

        if (!alive) return;
        setRows(cleanRows);
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setError(e?.message || "โหลดคอร์สเรียนของฉันไม่สำเร็จ");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [maxFetch]);

  const filteredRows = useMemo(() => {
    return rows.filter((course) => courseMatchesCategory(course, selectedCategory));
  }, [rows, selectedCategory]);

  const slideRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const initialScrollRef = useRef(null);

  useEffect(() => {
    const el = slideRef.current;
    if (!el) return;

    el.scrollLeft = 0;
    initialScrollRef.current = null;

    const updateArrows = () => {
      const now = slideRef.current;
      if (!now) return;

      const { scrollLeft, scrollWidth, clientWidth } = now;
      if (initialScrollRef.current === null) initialScrollRef.current = scrollLeft;

      if (scrollWidth <= clientWidth + 1) {
        setCanScrollLeft(false);
        setCanScrollRight(false);
        return;
      }

      const base = initialScrollRef.current ?? 0;
      const atStart = scrollLeft <= base + 1;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      setCanScrollLeft(!atStart);
      setCanScrollRight(!atEnd);
    };

    updateArrows();
    el.addEventListener("scroll", updateArrows);
    window.addEventListener("resize", updateArrows);

    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [filteredRows.length]);

  const handleSlideNext = () => {
    const el = slideRef.current;
    if (!el) return;
    el.scrollBy({ left: el.clientWidth * 0.8, behavior: "smooth" });
    setHasInteracted(true);
  };

  const handleSlidePrev = () => {
    const el = slideRef.current;
    if (!el) return;
    el.scrollBy({ left: -el.clientWidth * 0.8, behavior: "smooth" });
    setHasInteracted(true);
  };

  if (!auth?.currentUser) return null;
  if (!loading && !error && filteredRows.length === 0) return null;

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h3 className="mb-0 d-flex align-items-center gap-2">
          <BsPlayBtn />
          <strong>{title}</strong>
        </h3>

        <Link to={moreTo} style={{ textDecoration: "none" }}>
          <Button variant="link" className="p-0 text-decoration-none">
            {moreLabel} <span aria-hidden="true">›</span>
          </Button>
        </Link>
      </div>

      {loading && (
        <div className="text-center" style={{ marginTop: 40, marginBottom: 80 }}>
          <Spinner animation="border" role="status" />
        </div>
      )}

      {error && <div className="text-danger my-3">{error}</div>}

      {!loading && !error && filteredRows.length > 0 && (
        <div
          className={`slide-wrapper ${
            canScrollLeft || canScrollRight ? "has-scroll" : "no-scroll"
          }`}
        >
          {hasInteracted && canScrollLeft && (
            <button
              type="button"
              className="slide-arrow slide-arrow-left"
              onClick={handleSlidePrev}
              aria-label="เลื่อนซ้าย"
            >
              <BsChevronLeft size={22} />
            </button>
          )}

          <div
            className="article-slide-container"
            ref={slideRef}
            onTouchStart={() => setHasInteracted(true)}
            onMouseDown={() => setHasInteracted(true)}
          >
            {filteredRows.map((course) => {
              const percent = course.percentLabel;
              const isComplete = course.isComplete;

              return (
                <div key={course.id} className="slide-item">
                  <CourseAccessGate
                    courseId={course.id}
                    onAllowed={() => navigate(`/learn/${course.id}`)}
                  >
                    {({ openGate, checking }) => (
                      <InstallmentCourseEntryGuard
                        course={course}
                        onAllowed={openGate}
                      >
                        {({ handleEnter }) => (
                          <button
                            type="button"
                            onClick={handleEnter}
                            disabled={checking}
                            style={{
                              display: "block",
                              width: "100%",
                              padding: 0,
                              border: "none",
                              background: "transparent",
                              textAlign: "inherit",
                              color: "inherit",
                              cursor: checking ? "wait" : "pointer",
                            }}
                          >
                            <div className="card app-card h-100 rounded-4 overflow-hidden w-100">
                              <div
                                className="position-relative bg-surface"
                                style={{
                                  aspectRatio: "16 / 9",
                                  overflow: "hidden",
                                }}
                              >
                                {course.imageCoverUrl ? (
                                  <>
                                    <img
                                      src={course.imageCoverUrl}
                                      alt={course.title}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        display: "block",
                                      }}
                                    />

                                    <div
                                      className="d-flex align-items-center justify-content-center"
                                      style={{
                                        position: "absolute",
                                        inset: 0,
                                        pointerEvents: "none",
                                      }}
                                    >
                                      <div
                                        className="d-flex align-items-center justify-content-center rounded-circle"
                                        style={{
                                          width: 74,
                                          height: 74,
                                          background: "rgba(0,0,0,0.32)",
                                          backdropFilter: "blur(2px)",
                                        }}
                                      >
                                        <BsPlayCircle
                                          size={58}
                                          style={{
                                            color: "#fff",
                                            opacity: 0.98,
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </>
                                ) : (
                                  <div
                                    className="d-flex align-items-center justify-content-center h-100 text-secondary"
                                    style={{ fontSize: 14 }}
                                  >
                                    ไม่มีรูปปก
                                  </div>
                                )}
                              </div>

                              <div className="card-body p-3">
                                <div
                                  className="fw-semibold text-main mb-2"
                                  style={{
                                    fontSize: 15,
                                    lineHeight: 1.45,
                                    minHeight: 44,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  {course.title}
                                </div>

                                <div className="d-flex align-items-center justify-content-between mb-2">
                                  <small className="text-secondary">สถานะ</small>

                                  <small
                                    className={
                                      isComplete
                                        ? "text-success fw-semibold"
                                        : "text-primary fw-semibold"
                                    }
                                  >
                                    {isComplete ? "ครบถ้วน" : `ความก้าวหน้า ${percent}%`}
                                  </small>
                                </div>

                                <ProgressBar
                                  now={percent}
                                  variant={isComplete ? "success" : "primary"}
                                  style={{ height: 8, borderRadius: 999 }}
                                />
                              </div>
                            </div>
                          </button>
                        )}
                      </InstallmentCourseEntryGuard>
                    )}
                  </CourseAccessGate>
                </div>
              );
            })}
          </div>

          {canScrollRight && (
            <button
              type="button"
              className="slide-arrow slide-arrow-right"
              onClick={handleSlideNext}
              aria-label="เลื่อนขวา"
            >
              <BsChevronRight size={22} />
            </button>
          )}
        </div>
      )}

      <br />
    </>
  );
}